import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../../api";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "../ui/sonner";
import { STAFF, VALID_KAIROO_PERMISSIONS } from "../../constants/testIds";

const SENSITIVE = ["emulate", "secrets"];

// UX-CP-02 §2 SCR-P08-41 "granting emulate/secrets is type-to-confirm + audited" — the confirm
// input only unlocks when the pending change ADDS emulate or secrets (a role losing them, or a
// change touching neither, saves immediately — the friction is proportional to the risk).
export function EditRolePermissionsDialog({ role, open, onOpenChange }) {
  const [permissions, setPermissions] = useState([]);
  const [confirmText, setConfirmText] = useState("");
  const queryClient = useQueryClient();

  useEffect(() => {
    if (role) setPermissions(role.permissions);
  }, [role]);

  const addingSensitive = role ? SENSITIVE.filter((p) => permissions.includes(p) && !role.permissions.includes(p)) : [];
  const needsConfirm = addingSensitive.length > 0;

  const saveMutation = useMutation({
    mutationFn: () => api.patch(`/control/kairoo-roles/${role.id}/permissions`, { permissions }),
    onSuccess: () => {
      toast.success(`${role.name} permissions updated`);
      queryClient.invalidateQueries({ queryKey: ["kairoo-roles"] });
      queryClient.invalidateQueries({ queryKey: ["kairoo-users"] });
      setConfirmText("");
      onOpenChange(false);
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-modal" data-testid={STAFF.editPermissionsDialog}>
        <DialogHeader>
          <DialogTitle>Edit {role?.name} permissions</DialogTitle>
          <DialogDescription>Every operator with this role gets these permissions immediately.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {VALID_KAIROO_PERMISSIONS.map((perm) => (
            <label key={perm} className="flex items-center gap-2 text-sm">
              <Checkbox
                data-testid={STAFF.permissionCheckbox(perm)}
                checked={permissions.includes(perm)}
                onCheckedChange={(checked) => setPermissions(checked ? [...permissions, perm] : permissions.filter((p) => p !== perm))}
              />
              {perm} {SENSITIVE.includes(perm) && <span className="text-xs" style={{ color: "var(--status-overdue-text)" }}>(sensitive)</span>}
            </label>
          ))}
        </div>
        {needsConfirm && (
          <div className="space-y-1.5">
            <Label htmlFor="edit-perm-confirm">Type "{role?.name}" to confirm granting {addingSensitive.join(", ")}</Label>
            <Input id="edit-perm-confirm" data-testid={STAFF.editConfirmInput} value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={role?.name} />
          </div>
        )}
        <DialogFooter>
          <Button
            data-testid={STAFF.editConfirmSubmit}
            disabled={saveMutation.isPending || (needsConfirm && confirmText !== role?.name)}
            onClick={() => saveMutation.mutate()}
            className="text-white"
            style={{ background: "var(--scarlet-accent)" }}
          >
            Save permissions
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
